import TableC from "@/CommonElements/TableC";
import { useFilter } from "@/CommonElements/TableC/hooks/useFilter";
import BtnAsLink from "@/Component/BtnAsLink";
import { CountModal } from "@/Component/CountModal";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";
import { LabelValue } from "@/Component/Value";
import { useDeleteMakkahPilgrimGroupNameMappingMutation, useMakkahPilgrimGroupNameMappings } from "@/services/basicData";
import { useConfirm } from "@/utils/hooks/useConfrim";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { AddSquareIcon, DeleteIcon, Edit02Icon, NoteIcon, ViewIcon } from "@hugeicons/core-free-icons/index";
import { HugeiconsIcon } from "@hugeicons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Container } from "reactstrap";

export const MakkahPilgrimGroupNameMappings = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [searchValue, setSearchValue] = useState("");
    const { t } = useTranslation();
    const { getLocalizedText } = useLocalizedText();
    const { confirm, ModalComponent } = useConfirm();
    const {
        filters,
        handleFilterApply,
        handleFilterReset, } = useFilter()

    // ===============================
    // Side Effects
    // ===============================
    const { mutate: deleteMakkahPilgrimGroupNameMapping, isPending: isDeletingMakkahPilgrimGroupNameMapping } = useDeleteMakkahPilgrimGroupNameMappingMutation();
    const { data, isLoading } = useMakkahPilgrimGroupNameMappings({
        page: currentPage,
        per_page: 10,
        search: searchValue,
        ...filters,
    });

    const mappings = data?.data?.data?.data ?? [];
    const meta = data?.data?.data?.meta ?? {};

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleSearchChange = (e) => {
        setSearchValue(e.target.value);
        setCurrentPage(1);
    };

    const onFilterReset = () => {
        setCurrentPage(1);
        handleFilterReset()
    };

    const columns = [
        {
            name: t("ORGANIZER"),
            key: "mission_id",
            render: (value) => <SelectCachedOptions.Display
                value={value}
                optionKey="missions"
                campareBy="id"
                render={(option) => [option?.id, getLocalizedText(option?.name_ar, option?.name_en)].filter(Boolean).join(" - ")}
            />,
        },
        {
            name: t("AFFILIATED_ORGANIZER"),
            key: "company_id",
            render: (value) => <SelectCachedOptions.Display
                value={value}
                optionKey="companies"
                campareBy="id"
                render={(option) => [option?.id, getLocalizedText(option?.name_ar, option?.name_en)].filter(Boolean).join(" - ")}
            />,
        },
        {
            name: t("GROUP_NAME"),
            key: "raw_group_names",
            render: (value) => <CountModal
                count={value?.length}
                modal={{
                    props: {
                        size: "lg",
                        centered: true,
                        scrollable: true,
                    },
                    header: {
                        children: t("ITEMS_COUNT"),
                    },
                    footer: {},
                    body: {
                        children: (
                            <ul className="p-0 d-flex flex-column gap-2">
                                {value?.map((item) => (
                                    <li key={item.id} className="border py-2 px-1 rounded-2">
                                        <LabelValue.Value
                                            value={item}
                                        />
                                    </li>
                                ))}
                            </ul>
                        ),
                    },
                    props: {},
                }}
            >
                {value[0]}
            </CountModal>,
        },
        {
            name: t("MAPPED_GROUP_NAME"),
            key: "mapped_group_name",
        },
        {
            name: t("ACTIONS"),
            key: "actions",
        },
    ]

    const createButton = (
        <BtnAsLink
            className="btn btn-primary"
            link="/app/makkah/basic-data/makkah-pilgrim-group-name-mappings/create"
            icon={<HugeiconsIcon icon={AddSquareIcon} />}
        >
            {t("ADD")}
        </BtnAsLink>
    )

    const filterFields = [
        {
            name: "mission_id",
            label: t("ORGANIZER"),
            type: "select",
            optionKey: "missions",
            options: (mission) => ({
                value: mission.id,
                label: <LabelValue.Value value={[mission.id, getLocalizedText(mission.name_ar, mission.name_en)].join(" - ")} />,
            }),
        },
        (watch) => ({
            name: "company_id",
            label: t("AFFILIATED_ORGANIZER"),
            type: "select",
            filter: (c) => {
                const mission_id = watch("mission_id");
                return String(c.mission_id) === String(mission_id)
            },
            optionKey: "companies",
            options: (c) => ({
                value: c.id,
                label: <LabelValue.Value value={[c.id, getLocalizedText(c.name_ar, c.name_en)].join(" - ")} />,
            }),
        }),
    ]

    const handleDeleteClick = (id) => {
        confirm({
            title: t("CONFIRM_DELETE_PILGRIM_GROUP_NAME_MAPPINGS_TITLE"),
            content: t("CONFIRM_DELETE_PILGRIM_GROUP_NAME_MAPPINGS_MESSAGE"),
            okText: t("CONFIRM"),
            cancelText: t("CANCEL"),
            onOk: ({ closeModal }) => {
                deleteMakkahPilgrimGroupNameMapping(
                    { id },
                    {
                        onSuccess: () => {
                            closeModal();
                        },
                    }
                );

                return false;
            },
            onCancel: () => { },
        });
    };

    const getActions = (rowData) => {
        return [
            {
                key: "view",
                label: (
                    <Link
                        to={`${rowData.id}/view`}
                        className="d-flex align-items-center gap-2"
                    >
                        <HugeiconsIcon size={16} icon={ViewIcon} />
                        {t("VIEW")}
                    </Link>
                ),
            },
            {
                key: "edit",
                label: (
                    <Link
                        to={`${rowData.id}/edit`}
                        className="d-flex align-items-center gap-2"
                    >
                        <HugeiconsIcon size={16} icon={Edit02Icon} />
                        {t("EDIT")}
                    </Link>
                ),
            },
            {
                type: "divider",
            },
            {
                key: "delete",
                label: (
                    <div className="d-flex align-items-center gap-2">
                        <HugeiconsIcon size={16} icon={DeleteIcon} />
                        {t("DELETE")}
                    </div>
                ),
                danger: true,
                onClick: () => handleDeleteClick(rowData.id),
            },
        ]
    }

    return (
        <Container fluid>
            <ModalComponent loading={isDeletingMakkahPilgrimGroupNameMapping} />
            <TableC
                data={mappings || []}
                columns={columns}
                loading={isLoading}
                searchValue={searchValue}
                onSearchChange={handleSearchChange}
                searchPlaceholder={t("SEARCH_HERE")}
                pagination={{
                    current: currentPage || 1,
                    total: meta.total || 0,
                    pageSize: meta.per_page || 10,
                    onChange: handlePageChange,
                }}
                actions={getActions}
                renderActions={createButton}
                filterFields={filterFields}
                filters={filters}
                onFilterApply={handleFilterApply(() => setCurrentPage(1))}
                onFilterReset={onFilterReset}
            />
        </Container>
    );
};